import { i18n } from '@apps/main/modules/localization/localizationSetup';
import type {
  AmenityData,
  AmenityFilterType,
} from '@apps/main/new-desktop/modules/hotelFilters/model/hotelFilters';
import { HotelFilter } from '@apps/main/new-desktop/modules/hotelFilters/model/hotelFilters';

import { HotelAmenityType } from '@libs/trip-planner-interfaces';
import type { AppSvgIconType } from '@libs/ui';
import { assertValue } from '@libs/utils';

export interface HotelAmenityData {
  amenityType: HotelAmenityType;
  icon: AppSvgIconType;
  value: string;
}

const { t } = i18n.global;

export function getHotelAmenitiesConfig(): Record<
  HotelAmenityType,
  HotelAmenityData
> {
  return {
    [HotelAmenityType.IsAdultsOnly]: {
      amenityType: HotelAmenityType.IsAdultsOnly,
      icon: 'adultsOnly',
      value: t('filter.amenities.adults_only'),
    },
    [HotelAmenityType.HasAirConditioning]: {
      amenityType: HotelAmenityType.HasAirConditioning,
      icon: 'airConditioning',
      value: t('filter.amenities.air_conditioning'),
    },
    [HotelAmenityType.HasAllInclusive]: {
      amenityType: HotelAmenityType.HasAllInclusive,
      icon: 'allInclusive',
      value: t('filter.amenities.all_inclusive'),
    },
    [HotelAmenityType.HasBalcony]: {
      amenityType: HotelAmenityType.HasBalcony,
      icon: 'balcony',
      value: t('filter.amenities.balcony'),
    },
    [HotelAmenityType.HasBalconyOrTerrace]: {
      amenityType: HotelAmenityType.HasBalconyOrTerrace,
      icon: 'balcony',
      value: t('filter.amenities.balcony_or_terrace'),
    },
    [HotelAmenityType.HasBar]: {
      amenityType: HotelAmenityType.HasBar,
      icon: 'bar',
      value: t('filter.amenities.bar'),
    },
    [HotelAmenityType.HasBathtubOrShower]: {
      amenityType: HotelAmenityType.HasBathtubOrShower,
      icon: 'shower',
      value: t('filter.amenities.bathtub_or_shower'),
    },
    [HotelAmenityType.HasBoutique]: {
      amenityType: HotelAmenityType.HasBoutique,
      icon: 'boutique',
      value: t('filter.amenities.boutique'),
    },
    [HotelAmenityType.HasCableOrSatteliteTelevision]: {
      amenityType: HotelAmenityType.HasCableOrSatteliteTelevision,
      icon: 'tv',
      value: t('filter.amenities.cable_or_satellite_television'),
    },
    [HotelAmenityType.HasCarpet]: {
      amenityType: HotelAmenityType.HasCarpet,
      icon: 'carpet',
      value: t('filter.amenities.carpet'),
    },
    [HotelAmenityType.IsCastleOrMansion]: {
      amenityType: HotelAmenityType.IsCastleOrMansion,
      icon: 'castle',
      value: t('filter.amenities.castle_or_mansion'),
    },
    [HotelAmenityType.HasCeilingFan]: {
      amenityType: HotelAmenityType.HasCeilingFan,
      icon: 'ceilingFan',
      value: t('filter.amenities.ceiling_fan'),
    },
    [HotelAmenityType.HasChildBeds]: {
      amenityType: HotelAmenityType.HasChildBeds,
      icon: 'childBeds',
      value: t('filter.amenities.child_beds'),
    },
    [HotelAmenityType.HasChildChairs]: {
      amenityType: HotelAmenityType.HasChildChairs,
      icon: 'childChairs',
      value: t('filter.amenities.child_chairs'),
    },
    [HotelAmenityType.HasChildrensPool]: {
      amenityType: HotelAmenityType.HasChildrensPool,
      icon: 'pool',
      value: t('filter.amenities.childrens_pool'),
    },
    [HotelAmenityType.HasCoffeeMaker]: {
      amenityType: HotelAmenityType.HasCoffeeMaker,
      icon: 'coffeeMaker',
      value: t('filter.amenities.coffee_maker'),
    },
    [HotelAmenityType.HasConferenceRoom]: {
      amenityType: HotelAmenityType.HasConferenceRoom,
      icon: 'conferenceRoom',
      value: t('filter.amenities.conference_room'),
    },
    [HotelAmenityType.HasDesk]: {
      amenityType: HotelAmenityType.HasDesk,
      icon: 'desk',
      value: t('filter.amenities.desk'),
    },
    [HotelAmenityType.HasDishWasher]: {
      amenityType: HotelAmenityType.HasDishWasher,
      icon: 'dishwasher',
      value: t('filter.amenities.dish_washer'),
    },
    [HotelAmenityType.HasDryer]: {
      amenityType: HotelAmenityType.HasDryer,
      icon: 'hairDryer',
      value: t('filter.amenities.dryer'),
    },
    [HotelAmenityType.HasElectricCarCharger]: {
      amenityType: HotelAmenityType.HasElectricCarCharger,
      icon: 'electricCarCharger',
      value: t('filter.amenities.electric_car_charger'),
    },
    [HotelAmenityType.HasEngineHeater]: {
      amenityType: HotelAmenityType.HasEngineHeater,
      icon: 'engineHeater',
      value: t('filter.amenities.engine_heater'),
    },
    [HotelAmenityType.HasFirePlace]: {
      amenityType: HotelAmenityType.HasFirePlace,
      icon: 'fireplace',
      value: t('filter.amenities.fire_place'),
    },
    [HotelAmenityType.HasFreezer]: {
      amenityType: HotelAmenityType.HasFreezer,
      icon: 'freezer',
      value: t('filter.amenities.freezer'),
    },
    [HotelAmenityType.HasGarage]: {
      amenityType: HotelAmenityType.HasGarage,
      icon: 'garage',
      value: t('filter.amenities.garage'),
    },
    [HotelAmenityType.HasGarden]: {
      amenityType: HotelAmenityType.HasGarden,
      icon: 'garden',
      value: t('filter.amenities.garden'),
    },
    [HotelAmenityType.HasGym]: {
      amenityType: HotelAmenityType.HasGym,
      icon: 'gym',
      value: t('filter.amenities.gym'),
    },
    [HotelAmenityType.HasHairDryer]: {
      amenityType: HotelAmenityType.HasHairDryer,
      icon: 'hairDryer',
      value: t('filter.amenities.hair_dryer'),
    },
    [HotelAmenityType.HasInternet]: {
      amenityType: HotelAmenityType.HasInternet,
      icon: 'internet',
      value: t('filter.amenities.internet'),
    },
    [HotelAmenityType.HasIron]: {
      amenityType: HotelAmenityType.HasIron,
      icon: 'iron',
      value: t('filter.amenities.iron'),
    },
    [HotelAmenityType.HasIroningBoard]: {
      amenityType: HotelAmenityType.HasIroningBoard,
      icon: 'iron',
      value: t('filter.amenities.ironing_board'),
    },
    [HotelAmenityType.HasJacuzzi]: {
      amenityType: HotelAmenityType.HasJacuzzi,
      icon: 'jacuzzi',
      value: t('filter.amenities.jacuzzi'),
    },
    [HotelAmenityType.HasKettle]: {
      amenityType: HotelAmenityType.HasKettle,
      icon: 'kettle',
      value: t('filter.amenities.kettle'),
    },
    [HotelAmenityType.HasLakeView]: {
      amenityType: HotelAmenityType.HasLakeView,
      icon: 'lakeView',
      value: t('filter.amenities.lake_view'),
    },
    [HotelAmenityType.HasMicroWaveOven]: {
      amenityType: HotelAmenityType.HasMicroWaveOven,
      icon: 'microwave',
      value: t('filter.amenities.microwave_oven'),
    },
    [HotelAmenityType.HasMiniBar]: {
      amenityType: HotelAmenityType.HasMiniBar,
      icon: 'miniBar',
      value: t('filter.amenities.mini_bar'),
    },
    [HotelAmenityType.HasMountainView]: {
      amenityType: HotelAmenityType.HasMountainView,
      icon: 'mountainView',
      value: t('filter.amenities.mountain_view'),
    },
    [HotelAmenityType.HasOven]: {
      amenityType: HotelAmenityType.HasOven,
      icon: 'oven',
      value: t('filter.amenities.oven'),
    },
    [HotelAmenityType.HasParking]: {
      amenityType: HotelAmenityType.HasParking,
      icon: 'parking',
      value: t('filter.amenities.parking'),
    },
    [HotelAmenityType.HasPool]: {
      amenityType: HotelAmenityType.HasPool,
      icon: 'pool',
      value: t('filter.amenities.pool'),
    },
    [HotelAmenityType.HasPorch]: {
      amenityType: HotelAmenityType.HasPorch,
      icon: 'terrace',
      value: t('filter.amenities.porch'),
    },
    [HotelAmenityType.HasSafetyBox]: {
      amenityType: HotelAmenityType.HasSafetyBox,
      icon: 'safetyBox',
      value: t('filter.amenities.safety_box'),
    },
    [HotelAmenityType.HasSauna]: {
      amenityType: HotelAmenityType.HasSauna,
      icon: 'sauna',
      value: t('filter.amenities.sauna'),
    },
    [HotelAmenityType.HasSeaView]: {
      amenityType: HotelAmenityType.HasSeaView,
      icon: 'seaView',
      value: t('filter.amenities.sea_view'),
    },
    [HotelAmenityType.HasShower]: {
      amenityType: HotelAmenityType.HasShower,
      icon: 'shower',
      value: t('filter.amenities.shower'),
    },
    [HotelAmenityType.HasSkiInSkiOut]: {
      amenityType: HotelAmenityType.HasSkiInSkiOut,
      icon: 'ski',
      value: t('filter.amenities.ski_in_ski_out'),
    },
    [HotelAmenityType.HasSpa]: {
      amenityType: HotelAmenityType.HasSpa,
      icon: 'spa',
      value: t('filter.amenities.spa'),
    },
    [HotelAmenityType.HasSunbeds]: {
      amenityType: HotelAmenityType.HasSunbeds,
      icon: 'sunbeds',
      value: t('filter.amenities.sunbeds'),
    },
    [HotelAmenityType.HasTelephone]: {
      amenityType: HotelAmenityType.HasTelephone,
      icon: 'phone',
      value: t('filter.amenities.telephone'),
    },
    [HotelAmenityType.HasTelevision]: {
      amenityType: HotelAmenityType.HasTelevision,
      icon: 'tv',
      value: t('filter.amenities.television'),
    },
    [HotelAmenityType.HasTennis]: {
      amenityType: HotelAmenityType.HasTennis,
      icon: 'tennis',
      value: t('filter.amenities.tennis'),
    },
    [HotelAmenityType.HasTerrace]: {
      amenityType: HotelAmenityType.HasTerrace,
      icon: 'terrace',
      value: t('filter.amenities.terrace'),
    },
    [HotelAmenityType.HasToaster]: {
      amenityType: HotelAmenityType.HasToaster,
      icon: 'toaster',
      value: t('filter.amenities.toaster'),
    },
    [HotelAmenityType.HasVineyard]: {
      amenityType: HotelAmenityType.HasVineyard,
      icon: 'vineyard',
      value: t('filter.amenities.vineyard'),
    },
    [HotelAmenityType.HasWashingMachine]: {
      amenityType: HotelAmenityType.HasWashingMachine,
      icon: 'washingMachine',
      value: t('filter.amenities.washing_machine'),
    },
    [HotelAmenityType.IsBusinessProperty]: {
      amenityType: HotelAmenityType.IsBusinessProperty,
      icon: 'case',
      value: t('filter.amenities.business_property'),
    },
    [HotelAmenityType.IsFamilyProperty]: {
      amenityType: HotelAmenityType.IsFamilyProperty,
      icon: 'family',
      value: t('filter.amenities.family_property'),
    },
    [HotelAmenityType.IsGreenSustainableProperty]: {
      amenityType: HotelAmenityType.IsGreenSustainableProperty,
      icon: 'sustainable',
      value: t('filter.amenities.sustainable_property'),
    },
    [HotelAmenityType.IsSmokingAllowed]: {
      amenityType: HotelAmenityType.IsSmokingAllowed,
      icon: 'smoking',
      value: t('filter.amenities.smoking_allowed'),
    },
    [HotelAmenityType.IsSmokingDisallowed]: {
      amenityType: HotelAmenityType.IsSmokingDisallowed,
      icon: 'noSmoking',
      value: t('filter.amenities.smoking_disallowed'),
    },
    [HotelAmenityType.IsSunnySide]: {
      amenityType: HotelAmenityType.IsSunnySide,
      icon: 'sun',
      value: t('filter.amenities.sunny_side'),
    },
    [HotelAmenityType.PetsAreAllowed]: {
      amenityType: HotelAmenityType.PetsAreAllowed,
      icon: 'pets',
      value: t('filter.amenities.pets_allowed'),
    },
    [HotelAmenityType.PetsAreNotAllowed]: {
      amenityType: HotelAmenityType.PetsAreNotAllowed,
      icon: 'noPets',
      value: t('filter.amenities.pets_not_allowed'),
    },
  };
}

export const AMENITY_PRIORITIES: Record<HotelAmenityType, number> = {
  [HotelAmenityType.HasPool]: 1,
  [HotelAmenityType.HasInternet]: 2,
  [HotelAmenityType.HasAirConditioning]: 3,
  [HotelAmenityType.HasSpa]: 4,
  [HotelAmenityType.HasGym]: 5,
  [HotelAmenityType.HasParking]: 6,
  [HotelAmenityType.HasElectricCarCharger]: 7,
  [HotelAmenityType.HasSafetyBox]: 8,
  [HotelAmenityType.HasHairDryer]: 9,
  [HotelAmenityType.HasTelevision]: 10,
  [HotelAmenityType.HasBalconyOrTerrace]: 11,
  [HotelAmenityType.HasBalcony]: 12,
  [HotelAmenityType.HasTerrace]: 13,
  [HotelAmenityType.HasSeaView]: 14,
  [HotelAmenityType.HasMountainView]: 15,
  [HotelAmenityType.HasLakeView]: 16,
  [HotelAmenityType.HasJacuzzi]: 17,
  [HotelAmenityType.HasSauna]: 18,
  [HotelAmenityType.HasSunbeds]: 19,
  [HotelAmenityType.HasBathtubOrShower]: 20,
  [HotelAmenityType.HasShower]: 21,
  [HotelAmenityType.HasMiniBar]: 22,
  [HotelAmenityType.HasBar]: 23,
  [HotelAmenityType.HasCoffeeMaker]: 24,
  [HotelAmenityType.HasKettle]: 25,
  [HotelAmenityType.HasToaster]: 26,
  [HotelAmenityType.HasMicroWaveOven]: 27,
  [HotelAmenityType.HasOven]: 28,
  [HotelAmenityType.HasDishWasher]: 29,
  [HotelAmenityType.HasFreezer]: 30,
  [HotelAmenityType.HasChildrensPool]: 31,
  [HotelAmenityType.HasChildBeds]: 32,
  [HotelAmenityType.HasChildChairs]: 33,
  [HotelAmenityType.IsFamilyProperty]: 34,
  [HotelAmenityType.IsAdultsOnly]: 35,
  [HotelAmenityType.IsBusinessProperty]: 36,
  [HotelAmenityType.HasConferenceRoom]: 37,
  [HotelAmenityType.HasDesk]: 38,
  [HotelAmenityType.HasTelephone]: 39,
  [HotelAmenityType.IsGreenSustainableProperty]: 40,
  [HotelAmenityType.HasGarden]: 41,
  [HotelAmenityType.HasVineyard]: 42,
  [HotelAmenityType.HasBoutique]: 43,
  [HotelAmenityType.IsCastleOrMansion]: 44,
  [HotelAmenityType.HasTennis]: 45,
  [HotelAmenityType.HasSkiInSkiOut]: 46,
  [HotelAmenityType.IsSunnySide]: 47,
  [HotelAmenityType.HasPorch]: 48,
  [HotelAmenityType.HasCarpet]: 49,
  [HotelAmenityType.HasCeilingFan]: 50,
  [HotelAmenityType.HasFirePlace]: 51,
  [HotelAmenityType.HasGarage]: 52,
  [HotelAmenityType.HasIron]: 53,
  [HotelAmenityType.HasIroningBoard]: 54,
  [HotelAmenityType.HasWashingMachine]: 55,
  [HotelAmenityType.HasDryer]: 56,
  [HotelAmenityType.IsSmokingAllowed]: 57,
  [HotelAmenityType.IsSmokingDisallowed]: 58,
  [HotelAmenityType.PetsAreAllowed]: 59,
  [HotelAmenityType.PetsAreNotAllowed]: 60,
  [HotelAmenityType.HasCableOrSatteliteTelevision]: 61,
  [HotelAmenityType.HasAllInclusive]: 62,
  [HotelAmenityType.HasEngineHeater]: 63,
};

export function getAmenityPriority(amenityType: HotelAmenityType): number {
  const priority = AMENITY_PRIORITIES[amenityType];
  assertValue(priority, 'Impossible case! Missing priority for hotel amenity');
  return AMENITY_PRIORITIES[amenityType];
}

export function sortHotelAmenityTypesByPriority(
  amenityTypes: HotelAmenityType[]
): HotelAmenityType[] {
  return [...amenityTypes].sort((a, b) => {
    const priorityA = getAmenityPriority(a);
    const priorityB = getAmenityPriority(b);
    return priorityA - priorityB;
  });
}

export function sortHotelAmenityDataByPriority(
  amenities: HotelAmenityData[]
): HotelAmenityData[] {
  return [...amenities].sort((a, b) => {
    const priorityA = getAmenityPriority(a.amenityType);
    const priorityB = getAmenityPriority(b.amenityType);
    return priorityA - priorityB;
  });
}

export function sortAmenityDataByPriority(
  amenities: AmenityData[]
): AmenityData[] {
  return [...amenities].sort((a, b) => {
    const amenityA = hotelFilterToHotelAmenityType(a.type);
    const amenityB = hotelFilterToHotelAmenityType(b.type);

    const priorityA = amenityA ? getAmenityPriority(amenityA) : 1;
    const priorityB = amenityB ? getAmenityPriority(amenityB) : 1;

    return priorityA - priorityB;
  });
}

function hotelFilterToHotelAmenityType(
  filter: AmenityFilterType
): HotelAmenityType | undefined {
  switch (filter) {
    case HotelFilter.AmenitiesPool:
      return HotelAmenityType.HasPool;
    case HotelFilter.AmenitiesWifi:
      return HotelAmenityType.HasInternet;
    case HotelFilter.AmenitiesAirConditioned:
      return HotelAmenityType.HasAirConditioning;
    case HotelFilter.AmenitiesSpa:
      return HotelAmenityType.HasSpa;
    case HotelFilter.AmenitiesGym:
      return HotelAmenityType.HasGym;
    case HotelFilter.AmenitiesParking:
      return HotelAmenityType.HasParking;
    case HotelFilter.AmenitiesVehicleChargingStation:
      return HotelAmenityType.HasElectricCarCharger;
    default:
      return undefined;
  }
}
